/**
 * Skylite Desktop OS Interface
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 * 
 * This file and its contents are protected by copyright law.
 * Unauthorized reproduction or distribution is prohibited.
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  IconButton, 
  Menu, 
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import jsPDF from 'jspdf';
import { fileSystem } from '../services/fileSystem';

/** Primary localStorage key; legacy `sphinxReportData` still read once for migration. */
const REPORT_STORAGE_KEY = 'skyliteReportData';
const LEGACY_REPORT_STORAGE_KEY = 'sphinxReportData';

const ReportSummary = () => {
  const [reportData, setReportData] = useState({
    studentName: '',
    date: new Date().toLocaleDateString(),
    className: '',
    executiveSummary: '',
    scopeTargets: '',
    scopeRules: '',
    reconnaissance: '',
    weaponizationDelivery: '',
    exploitation: '',
    installation: '',
    commandControl: '',
    actionsObjectives: '',
    recommendations: '',
    reflection: '',
    toolsUsed: '',
    vocabulary: ''
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    // Load saved report data from database
    const loadReportData = async () => {
      try {
        const response = await fetch('http://localhost:3005/api/reports/get', {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const savedData = await response.json();
          if (savedData) {
            setReportData({
              studentName: savedData.StudentName || '',
              date: savedData.Date || new Date().toLocaleDateString(),
              className: savedData.ClassName || '',
              executiveSummary: savedData.ExecutiveSummary || '',
              scopeTargets: savedData.ScopeTargets || '',
              scopeRules: savedData.ScopeRules || '',
              reconnaissance: savedData.Reconnaissance || '',
              weaponizationDelivery: savedData.WeaponizationDelivery || '',
              exploitation: savedData.Exploitation || '',
              installation: savedData.Installation || '',
              commandControl: savedData.CommandControl || '',
              actionsObjectives: savedData.ActionsObjectives || '',
              recommendations: savedData.Recommendations || '',
              reflection: savedData.Reflection || '',
              toolsUsed: savedData.ToolsUsed || '',
              vocabulary: savedData.Vocabulary || ''
            });
          }
        }
      } catch (error) {
        // Fallback to localStorage if database fails
        const raw =
          localStorage.getItem(REPORT_STORAGE_KEY) ||
          localStorage.getItem(LEGACY_REPORT_STORAGE_KEY);
        if (raw) {
          try {
            setReportData(JSON.parse(raw));
          } catch (localError) {
            // Silent fallback
          }
        }
      }
    };

    loadReportData();
  }, []);

  const handleInputChange = (field, value) => {
    const newData = { ...reportData, [field]: value };
    setReportData(newData);
    // Auto-save to localStorage (migrate off legacy key)
    localStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify(newData));
    localStorage.removeItem(LEGACY_REPORT_STORAGE_KEY);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSave = async () => {
    try {
      const response = await fetch('http://localhost:3005/api/reports/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        // Save to database successful, now save as file
        try {
          const savedFile = await fileSystem.saveReport(reportData);
          setSnackbar({ 
            open: true, 
            message: `Report saved successfully! File created: ${savedFile.name}`, 
            severity: 'success' 
          });
        } catch (fileError) {
          setSnackbar({ 
            open: true, 
            message: 'Report saved to database but failed to create file', 
            severity: 'warning' 
          });
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to save report', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Error saving report', severity: 'error' });
    }
    handleMenuClose();
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    
    // Set font sizes
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    
    // Title
    doc.text('Risk Assessment', 20, 20);
    
    // Header info
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`By: ${reportData.studentName}`, 20, 35);
    doc.text(`Date: ${reportData.date}`, 20, 42);
    doc.text(`Class: ${reportData.className}`, 20, 49);
    
    let yPosition = 70;
    
    // Function to add section
    const addSection = (title, content) => {
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 20, yPosition);
      yPosition += 10;
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      
      // Split content into lines that fit the page width
      const lines = doc.splitTextToSize(content || 'No content provided', 170);
      doc.text(lines, 20, yPosition);
      yPosition += (lines.length * 7) + 10;
    };
    
    // Add all sections
    addSection('1. Executive Summary', reportData.executiveSummary);
    addSection('2. Scope of the Test', `Targets: ${reportData.scopeTargets}\nRules: ${reportData.scopeRules}`);
    addSection('3. Reconnaissance', reportData.reconnaissance);
    addSection('4. Weaponization and Delivery', reportData.weaponizationDelivery);
    addSection('5. Exploitation', reportData.exploitation);
    addSection('6. Installation', reportData.installation);
    addSection('7. Command and Control', reportData.commandControl);
    addSection('8. Actions on Objectives', reportData.actionsObjectives);
    addSection('9. Recommendations', reportData.recommendations);
    addSection('10. Reflection', reportData.reflection);
    addSection('Appendix - Tools Used', reportData.toolsUsed);
    addSection('Appendix - Vocabulary', reportData.vocabulary);
    
    // Generate PDF data
    const pdfData = doc.output('arraybuffer');
    const timestamp = new Date().toISOString().split('T')[0];
    const fileName = `Risk_Assessment_${timestamp}.pdf`;
    
    // Save PDF to file system
    try {
      fileSystem.saveFile(fileName, pdfData, 'Documents', 'application/pdf');
    } catch (fileError) {
      // Silent error handling
    }
    
    // Save the PDF for download
    doc.save(fileName);
    setSnackbar({ 
      open: true, 
      message: `PDF downloaded and saved as ${fileName}`, 
      severity: 'success' 
    });
    handleMenuClose();
  };

  const sectionStyle = {
    marginBottom: 3,
    '& .MuiTypography-root': {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#FFD700',
      marginBottom: 1
    },
    '& .MuiTextField-root': {
      '& .MuiInputBase-root': {
        fontSize: '12px',
        color: '#fff',
        '& textarea': {
          fontSize: '12px',
          color: '#fff'
        }
      },
      '& .MuiInputLabel-root': {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)'
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: 'rgba(255, 255, 255, 0.3)'
        },
        '&:hover fieldset': {
          borderColor: 'rgba(255, 255, 255, 0.5)'
        },
        '&.Mui-focused fieldset': {
          borderColor: '#FFD700'
        }
      }
    }
  };

  return (
    <Box sx={{ 
      height: '100%', 
      backgroundColor: '#1a1a1a',
      color: '#fff',
      padding: 2,
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* Header with menu */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        pb: 1,
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <Typography variant="h6" sx={{ color: '#FFD700', fontSize: '18px' }}>
          Risk Assessment
        </Typography>
        <IconButton
          onClick={handleMenuOpen}
          sx={{ color: '#FFD700' }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(30, 30, 30, 0.95)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              '& .MuiMenuItem-root': {
                color: '#FFD700',
                '&:hover': {
                  backgroundColor: 'rgba(255, 215, 0, 0.1)'
                }
              }
            }
          }}
        >
          <MenuItem onClick={handleSave}>
            <SaveIcon sx={{ mr: 1 }} />
            Save Report
          </MenuItem>
          <MenuItem onClick={handleDownloadPDF}>
            <DownloadIcon sx={{ mr: 1 }} />
            Download PDF
          </MenuItem>
        </Menu>
      </Box>

      {/* Report Form */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header Information */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Your Name"
            value={reportData.studentName}
            onChange={(e) => handleInputChange('studentName', e.target.value)}
            size="small"
            sx={{ flex: 1, ...sectionStyle }}
          />
          <TextField
            label="Date"
            value={reportData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            size="small"
            sx={{ flex: 1, ...sectionStyle }}
          />
          <TextField
            label="Class Name"
            value={reportData.className}
            onChange={(e) => handleInputChange('className', e.target.value)}
            size="small"
            sx={{ flex: 1, ...sectionStyle }}
          />
        </Box>

        {/* Executive Summary */}
        <Box sx={sectionStyle}>
          <Typography>1. Executive Summary</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            Explain in a few sentences what system or website you tested, what the goal was, and what you discovered.
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.executiveSummary}
            onChange={(e) => handleInputChange('executiveSummary', e.target.value)}
            fullWidth
            placeholder="Describe what you tested and what you found..."
          />
        </Box>

        {/* Scope */}
        <Box sx={sectionStyle}>
          <Typography>2. Scope of the Test</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            What was tested and what rules you followed.
          </Typography>
          <TextField
            label="Targets (What you tested)"
            multiline
            rows={2}
            value={reportData.scopeTargets}
            onChange={(e) => handleInputChange('scopeTargets', e.target.value)}
            fullWidth
            placeholder="e.g., Website, computer system, network..."
          />
          <TextField
            label="Rules (What you were allowed/not allowed to do)"
            multiline
            rows={2}
            value={reportData.scopeRules}
            onChange={(e) => handleInputChange('scopeRules', e.target.value)}
            fullWidth
            placeholder="e.g., No deleting files, only scan between 10-11am..."
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Reconnaissance */}
        <Box sx={sectionStyle}>
          <Typography>3. Reconnaissance</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            How did you learn about the target? What information did you find before starting?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.reconnaissance}
            onChange={(e) => handleInputChange('reconnaissance', e.target.value)}
            fullWidth
            placeholder="Describe your research and information gathering..."
          />
        </Box>

        {/* Weaponization and Delivery */}
        <Box sx={sectionStyle}>
          <Typography>4. Weaponization and Delivery</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            What tools did you prepare and how did you use them?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.weaponizationDelivery}
            onChange={(e) => handleInputChange('weaponizationDelivery', e.target.value)}
            fullWidth
            placeholder="Describe the tools you used and how you accessed the system..."
          />
        </Box>

        {/* Exploitation */}
        <Box sx={sectionStyle}>
          <Typography>5. Exploitation</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            How did you get in? What weakness or bug did you find?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.exploitation}
            onChange={(e) => handleInputChange('exploitation', e.target.value)}
            fullWidth
            placeholder="Describe how you exploited the vulnerability..."
          />
        </Box>

        {/* Installation */}
        <Box sx={sectionStyle}>
          <Typography>6. Installation</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            Did you stay in the system? Did you create a backdoor or user account?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.installation}
            onChange={(e) => handleInputChange('installation', e.target.value)}
            fullWidth
            placeholder="Describe if and how you maintained access..."
          />
        </Box>

        {/* Command and Control */}
        <Box sx={sectionStyle}>
          <Typography>7. Command and Control</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            Could you control the system? Did you send commands remotely?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.commandControl}
            onChange={(e) => handleInputChange('commandControl', e.target.value)}
            fullWidth
            placeholder="Describe your control over the system..."
          />
        </Box>

        {/* Actions on Objectives */}
        <Box sx={sectionStyle}>
          <Typography>8. Actions on Objectives</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            What could you have done to the system? Could you take information or break something?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.actionsObjectives}
            onChange={(e) => handleInputChange('actionsObjectives', e.target.value)}
            fullWidth
            placeholder="Describe what you could accomplish once inside..."
          />
        </Box>

        {/* Recommendations */}
        <Box sx={sectionStyle}>
          <Typography>9. Recommendations</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            What should be done to stay safe? List the top 3-5 things the system owner can do.
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.recommendations}
            onChange={(e) => handleInputChange('recommendations', e.target.value)}
            fullWidth
            placeholder="e.g., Use stronger passwords, update software, block Port 22..."
          />
        </Box>

        {/* Reflection */}
        <Box sx={sectionStyle}>
          <Typography>10. Reflection</Typography>
          <Typography variant="body2" sx={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)', mb: 1 }}>
            What did you learn? What was most interesting? What would you do differently?
          </Typography>
          <TextField
            multiline
            rows={3}
            value={reportData.reflection}
            onChange={(e) => handleInputChange('reflection', e.target.value)}
            fullWidth
            placeholder="Share your thoughts and lessons learned..."
          />
        </Box>

        {/* Appendix */}
        <Box sx={sectionStyle}>
          <Typography>Appendix - Tools Used</Typography>
          <TextField
            multiline
            rows={2}
            value={reportData.toolsUsed}
            onChange={(e) => handleInputChange('toolsUsed', e.target.value)}
            fullWidth
            placeholder="List any tools or websites you used for the test..."
          />
        </Box>

        <Box sx={sectionStyle}>
          <Typography>Appendix - Vocabulary</Typography>
          <TextField
            multiline
            rows={2}
            value={reportData.vocabulary}
            onChange={(e) => handleInputChange('vocabulary', e.target.value)}
            fullWidth
            placeholder="Explain any big words you used in your report..."
          />
        </Box>
      </Box>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportSummary;